import { useEffect, useRef } from "react";
import { Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtAZN } from "@/lib/api";
import { useAviator } from "./useAviator";
import { FlightCanvas } from "./FlightCanvas";
import { BetPanel } from "./BetPanel";
import { engine, sfx } from "@/lib/sound";

const chipCls = (v) => (v < 2 ? "text-sky-400" : v < 10 ? "text-fuchsia-400" : "text-[#FFD700]");

export const AviatorGame = ({ mode }) => {
  const { s, m, now, poll } = useAviator();
  const { refresh } = useAuth();
  const lastPhase = useRef(null);
  const eng = useRef(null);

  useEffect(() => {
    const p = s?.phase;
    if (p === "flying" && !eng.current) eng.current = engine();
    if (p !== "flying" && eng.current) { eng.current.stop(); eng.current = null; }
    if (p === "crashed" && lastPhase.current === "flying") sfx.explode();
  }, [s?.phase]);

  useEffect(() => { eng.current?.set(m); }, [m]);
  useEffect(() => () => eng.current?.stop(), []);

  useEffect(() => {
    if (s?.phase === "crashed" && lastPhase.current !== "crashed") refresh();
    lastPhase.current = s?.phase;
  }, [s?.phase, refresh]);

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-5" data-testid="aviator-game">
      <div className="space-y-4 min-w-0">
        <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="aviator-history">
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-red-400 shrink-0"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Canlı</span>
          {(s?.history || []).map((h, i) => (
            <span key={i} className={`shrink-0 rounded-full bg-white/5 px-3 py-1 text-xs font-semibold ${chipCls(h)}`}>{h.toFixed(2)}x</span>
          ))}
        </div>
        <FlightCanvas s={s} m={m} now={now} />
        <div className="grid md:grid-cols-2 gap-4">
          <BetPanel slot={0} s={s} m={m} mode={mode} poll={poll} />
          <BetPanel slot={1} s={s} m={m} mode={mode} poll={poll} />
        </div>
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#111]/90 backdrop-blur-xl p-4 h-fit" data-testid="aviator-live-bets">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display font-semibold">Canlı mərclər</p>
          <span className="flex items-center gap-1.5 text-xs text-zinc-400" data-testid="aviator-players"><Users size={14} /> {s?.players || 0}</span>
        </div>
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 text-[10px] uppercase tracking-[0.15em] text-zinc-500 pb-2 border-b border-white/10">
          <span>Oyunçu</span><span>Mərc</span><span>Qazanc</span>
        </div>
        <div className="max-h-[420px] overflow-y-auto divide-y divide-white/5">
          {(s?.bets || []).length === 0 && <p className="text-xs text-zinc-500 py-6 text-center">Bu raundda real mərc yoxdur</p>}
          {(s?.bets || []).map((b) => (
            <div key={b.id} className={`grid grid-cols-[1fr_auto_auto] gap-x-3 py-2 text-sm ${b.cashed_at ? "text-emerald-400" : ""}`}>
              <span className="truncate">{b.name}</span>
              <span>{fmtAZN(b.amount)}</span>
              <span>{b.cashed_at ? `x${b.cashed_at} · ${fmtAZN(b.win)}` : "-"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
